import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import {
  deleteApplicationRole,
  listApplicationRoles,
} from "../../Apis/ApplicationAdminUserApis";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  isWalletAdminUser,
  WALLET_ADMIN_ACTION,
} from "../../Utils/walletAdminAccess";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import useAuthStore from "../../Zustand/Store/useAuthStore";

export default function ApplicationUserRolesList() {
  const navigate = useNavigate();
  const { Permission } = usePermissionDataStore();
  const { userData } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);

  const walletAdmin = isWalletAdminUser(userData, Permission);
  const canAdd =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_ADD") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canEdit =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_EDIT") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canDelete =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ROLE_DELETE") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(await listApplicationRoles());
    } catch (err) {
      toast.error(err.message || "Failed to load roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (role) => {
    if (!canDelete) return;
    if (
      !window.confirm(
        `Delete role "${role.user_role_name || role.role_name}"?`
      )
    ) {
      return;
    }
    try {
      await deleteApplicationRole(role.application_user_role_id);
      toast.success("Role deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1000 }}>
      <Layout4
        heading="User Roles"
        subtitle="Application role catalog"
        showAddButton={canAdd}
        btnName="Add role"
        Route="/application/user-roles"
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary" py={2}>
                      No roles yet. Run WalletAdminRbacSeeder or add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.application_user_role_id} hover>
                    <TableCell>{role.application_user_role_id}</TableCell>
                    <TableCell>
                      {role.user_role_name || role.role_name}
                    </TableCell>
                    <TableCell>{role.description || "—"}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/application/user-roles/edit/${role.application_user_role_id}`
                              )
                            }
                          >
                            <Pencil size={16} />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(role)}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
